import React, { forwardRef, useImperativeHandle, useState } from "react";
import {
  Box,
  Chip,
  Divider,
  Fade,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  Slider,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import {
  AccessTime,
  AccountCircle,
  CalendarToday,
  Email,
  FitnessCenter,
  PeopleAlt,
  Phone,
  Straighten,
} from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { GlassPanel, ModernTextField } from "./styledComponents";

const allowOnlyLetters = (e) => {
  if (!/[A-Za-zÀ-ÿ\s]/.test(e.key)) {
    e.preventDefault();
  }
};

const allowOnlyNumbers = (e) => {
  if (!/[0-9]/.test(e.key)) {
    e.preventDefault();
  }
};

const allowNumbersAndDot = (e) => {
  if (!/[0-9.]/.test(e.key)) {
    e.preventDefault();
  }
};

const today = new Date();
const yyyy = today.getFullYear();
const mm = String(today.getMonth() + 1).padStart(2, "0");
const dd = String(today.getDate()).padStart(2, "0");
const maxDate = `${yyyy}-${mm}-${dd}`;
const MIN_BIRTHDATE_STRING = "1920-01-01";
const MIN_BIRTHDATE = new Date(MIN_BIRTHDATE_STRING);
const MIN_HEIGHT_CM = 20;
const MAX_HEIGHT_CM = 220;

const formatMeters = (cmValue) => (cmValue / 100).toFixed(2);

const Paso1InformacionBasica = forwardRef(
  ({ formValues, onChange, formValuesTelefono }, ref) => {
    const [openError, setOpenError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [firstValidationError, setFirstValidationError] = useState("");

    const handleCloseError = () => {
      setOpenError(false);
    };

    const handleFieldChange = (name, value) => {
      onChange(name, value);
      if (firstValidationError === name) {
        setFirstValidationError("");
      }
    };

    const handleInputChange = (e) => {
      const { name, value } = e.target;
      handleFieldChange(name, value);
    };

    const parsedHeight = Number(formValues.estatura);
    const heightValue = Number.isFinite(parsedHeight)
      ? Math.min(Math.max(parsedHeight, MIN_HEIGHT_CM), MAX_HEIGHT_CM)
      : MIN_HEIGHT_CM;

    const validateField = (fieldName, value) => {
      const soloLetras = /^[A-Za-zÀ-ÿ\s]+$/;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const hoy = new Date();

      switch (fieldName) {
        case "nombrePaciente":
          if (!value) return "Falta el campo Nombre(s)";
          if (!soloLetras.test(value) || value.length > 50) {
            return "El campo Nombre(s) es inválido. Solo letras y máximo 50 caracteres.";
          }
          break;

        case "apellidoPaciente":
          if (!value) return "Falta el campo Apellidos";
          if (!soloLetras.test(value) || value.length > 50) {
            return "El campo Apellidos es inválido. Solo letras y máximo 50 caracteres.";
          }
          break;

        case "telefono": {
          const telefono = formValuesTelefono || value;
          if (!telefono) return "Falta el campo Teléfono";
          if (!/^[0-9]{10}$/.test(telefono)) {
            return "El campo Teléfono es inválido. Debe tener exactamente 10 dígitos numéricos.";
          }
          break;
        }

        case "email": {
          const trimmedEmail = (value || "").trim();
          if (!trimmedEmail) return null;
          if (trimmedEmail.length > 50) {
            return "El campo Correo Electrónico es inválido. Máximo 50 caracteres.";
          }
          if (!emailRegex.test(trimmedEmail)) {
            return "Ingresa un correo electrónico válido.";
          }
          break;
        }

        case "pesoPaciente":
          if (!value) return "Falta el campo Peso";
          if (!/^[0-9.]+$/.test(value)) {
            return "El campo Peso es inválido. Solo números y punto.";
          }
          break;

        case "estatura": {
          if (value === undefined || value === null || value === "") {
            return "Falta el campo Estatura";
          }
          const numericValue = Number(value);
          if (
            Number.isNaN(numericValue) ||
            numericValue < MIN_HEIGHT_CM ||
            numericValue > MAX_HEIGHT_CM
          ) {
            return "Ingresa una estatura válida.";
          }
          break;
        }

        case "fechaNacimiento":
          if (!value) return "Falta el campo Fecha de Nacimiento";
          const fechaNacimiento = new Date(value);
          if (fechaNacimiento > hoy || fechaNacimiento < MIN_BIRTHDATE) {
            return "Ingresa una fecha válida.";
          }
          break;

        default:
          break;
      }

      return null;
    };

    const fieldsToValidate = [
      "nombrePaciente",
      "apellidoPaciente",
      "telefono",
      "email",
      "pesoPaciente",
      "estatura",
      "fechaNacimiento",
      "sexo",
      "embarazoActivo",
      "tiempoEmbarazo",
    ];

    const validateAll = () => {
      for (const field of fieldsToValidate) {
        const error = validateField(field, formValues[field]);
        if (error) {
          setFirstValidationError(field);
          setErrorMessage(error);
          setOpenError(true);
          return false;
        }
      }
      return true;
    };

    useImperativeHandle(ref, () => ({
      validate: () => {
        const isValid = validateAll();
        if (isValid) {
          setErrorMessage("Validación exitosa. Puedes avanzar.");
          setOpenError(true);
        }
        return isValid;
      },
    }));

    return (
      <>
        <Fade in>
          <GlassPanel>
            <Stack spacing={3}>
              <Box textAlign="center">
                <Typography variant="overline" sx={{ letterSpacing: 3 }}>
                  Paso 1
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: "bold", color: "primary.main" }}
                >
                  Información básica
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 1 }}>
                  Completa tus datos personales y de contacto para crear el
                  expediente digital.
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="center"
                  mt={1}
                >
                  {["Identidad", "Contacto", "Bienestar"].map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Stack>
              </Box>

              <Divider />

              <Box sx={{ width: "100%", px: { xs: 0, sm: 0.5, md: 1.5 } }}>
                <Grid
                  container
                  rowSpacing={3}
                  columnSpacing={{ xs: 0, sm: 2, md: 3 }}
                  alignItems="flex-start"
                >
                  <Grid item xs={12} md={6}>
                    <ModernTextField
                      label="Nombre(s)"
                      name="nombrePaciente"
                      value={formValues.nombrePaciente || ""}
                      onChange={handleInputChange}
                      required
                      inputProps={{ maxLength: 50 }}
                      onKeyPress={allowOnlyLetters}
                      error={firstValidationError === "nombrePaciente"}
                      helperText={
                        firstValidationError === "nombrePaciente"
                          ? errorMessage
                          : ""
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AccountCircle />
                          </InputAdornment>
                        ),
                      }}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <ModernTextField
                      label="Apellidos"
                      name="apellidoPaciente"
                      value={formValues.apellidoPaciente || ""}
                      onChange={handleInputChange}
                      required
                      inputProps={{ maxLength: 50 }}
                      onKeyPress={allowOnlyLetters}
                      error={firstValidationError === "apellidoPaciente"}
                      helperText={
                        firstValidationError === "apellidoPaciente"
                          ? errorMessage
                          : ""
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PeopleAlt />
                          </InputAdornment>
                        ),
                      }}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <ModernTextField
                      label="Teléfono"
                      name="telefono"
                      value={formValues.telefono || formValuesTelefono || ""}
                      onChange={handleInputChange}
                      required
                      disabled
                      inputProps={{ maxLength: 10 }}
                      onKeyPress={allowOnlyNumbers}
                      error={firstValidationError === "telefono"}
                      helperText={
                        firstValidationError === "telefono" ? errorMessage : ""
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Phone />
                          </InputAdornment>
                        ),
                      }}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <ModernTextField
                      label="Correo Electrónico"
                      name="email"
                      type="email"
                      value={formValues.email || ""}
                      onChange={handleInputChange}
                      inputProps={{ maxLength: 50 }}
                      error={firstValidationError === "email"}
                      helperText={
                        firstValidationError === "email" ? errorMessage : ""
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email />
                          </InputAdornment>
                        ),
                      }}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <ModernTextField
                      label="Peso (Kg)"
                      name="pesoPaciente"
                      value={formValues.pesoPaciente || ""}
                      onChange={handleInputChange}
                      required
                      inputProps={{ maxLength: 6 }}
                      onKeyPress={allowNumbersAndDot}
                      error={firstValidationError === "pesoPaciente"}
                      helperText={
                        firstValidationError === "pesoPaciente"
                          ? errorMessage
                          : "Ingresa tu peso en kilogramos, ej. 80.5"
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <FitnessCenter />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography variant="body2" color="text.secondary">
                              Kg
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <ModernTextField
                      label="Fecha de Nacimiento"
                      name="fechaNacimiento"
                      type="date"
                      value={formValues.fechaNacimiento || ""}
                      onChange={handleInputChange}
                      required
                      inputProps={{
                        max: maxDate,
                        min: MIN_BIRTHDATE_STRING,
                      }}
                      error={firstValidationError === "fechaNacimiento"}
                      helperText={
                        firstValidationError === "fechaNacimiento"
                          ? errorMessage
                          : ""
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarToday />
                          </InputAdornment>
                        ),
                      }}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        borderRadius: 3,
                        p: 2.5,
                      }}
                    >
                      <Stack spacing={1.25}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Straighten color="primary" />
                          <Typography fontWeight={600}>Estatura</Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {`${heightValue} cm · ${formatMeters(heightValue)} m`}
                        </Typography>
                        <Slider
                          value={heightValue}
                          min={MIN_HEIGHT_CM}
                          max={MAX_HEIGHT_CM}
                          step={1}
                          valueLabelDisplay="auto"
                          onChange={(_, newValue) =>
                            handleFieldChange("estatura", String(newValue))
                          }
                          aria-label="Selecciona tu estatura en centímetros"
                        />
                        <Typography variant="caption" color="text.secondary">
                          Ajusta el control para registrar tu estatura.
                        </Typography>
                        {firstValidationError === "estatura" && (
                          <Typography variant="caption" color="error">
                            {errorMessage}
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl
                      component="fieldset"
                      required
                      error={firstValidationError === "sexo"}
                      sx={{ width: "100%" }}
                    >
                      <FormLabel component="legend">Sexo</FormLabel>
                      <RadioGroup
                        row
                        name="sexo"
                        value={formValues.sexo || ""}
                        onChange={handleInputChange}
                      >
                        <FormControlLabel
                          value="M"
                          control={<Radio />}
                          label="Masculino"
                        />
                        <FormControlLabel
                          value="F"
                          control={<Radio />}
                          label="Femenino"
                        />
                      </RadioGroup>
                      {firstValidationError === "sexo" && (
                        <FormHelperText>
                          Por favor selecciona tu sexo.
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  {formValues.sexo === "F" && (
                    <>
                      <Grid item xs={12} md={6}>
                        <FormControl
                          component="fieldset"
                          required
                          error={firstValidationError === "embarazoActivo"}
                        >
                          <FormLabel component="legend">
                            ¿Estás embarazada?
                          </FormLabel>
                          <RadioGroup
                            row
                            name="embarazoActivo"
                            value={formValues.embarazoActivo || ""}
                            onChange={handleInputChange}
                          >
                            <FormControlLabel
                              value="si"
                              control={<Radio />}
                              label="Sí"
                            />
                            <FormControlLabel
                              value="no"
                              control={<Radio />}
                              label="No"
                            />
                          </RadioGroup>
                          {firstValidationError === "embarazoActivo" && (
                            <FormHelperText>
                              Por favor indica si estás embarazada.
                            </FormHelperText>
                          )}
                        </FormControl>
                      </Grid>

                      {formValues.embarazoActivo === "si" && (
                        <Grid item xs={12} md={6}>
                          <ModernTextField
                            label="Tiempo de embarazo (semanas)"
                            name="tiempoEmbarazo"
                            value={formValues.tiempoEmbarazo || ""}
                            onChange={handleInputChange}
                            required
                            inputProps={{ maxLength: 2 }}
                            onKeyPress={allowOnlyNumbers}
                            error={firstValidationError === "tiempoEmbarazo"}
                            helperText={
                              firstValidationError === "tiempoEmbarazo"
                                ? errorMessage
                                : ""
                            }
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <AccessTime />
                                </InputAdornment>
                              ),
                            }}
                            fullWidth
                          />
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
              </Box>
            </Stack>
          </GlassPanel>
        </Fade>

        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          open={openError}
          autoHideDuration={6000}
          onClose={handleCloseError}
          message={errorMessage}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleCloseError}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        />
      </>
    );
  }
);

export default Paso1InformacionBasica;
